
import { MessageTone } from "@/types/message";
import { useToast } from "../use-toast";
import { Conversation } from "@/types/conversation";
import { DBMessage } from "@/types/message";

export const useRegenerateResponse = (id: string | undefined, refreshMessages: () => Promise<DBMessage[] | null>) => {
  const { toast } = useToast();

  const regenerateAIResponse = async (
    conversation: Conversation | null,
    messages: DBMessage[],
    messageId: string,
    tone: MessageTone,
    setMessages: React.Dispatch<React.SetStateAction<DBMessage[]>>,
    handleRegenerateAIResponse: (
      conversation: Conversation,
      messages: DBMessage[],
      messageId: string,
      tone: MessageTone,
      customInstructions?: string
    ) => Promise<DBMessage | null>,
    customInstructions?: string
  ) => {
    console.log("Starting regeneration for message:", messageId, "with tone:", tone);
    console.log("Custom instructions:", customInstructions || "none");
    
    if (!conversation) {
      console.error("Cannot regenerate: conversation is null");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Unable to regenerate the message because the conversation could not be loaded."
      });
      return null;
    }
    
    try {
      // Validate that the message to regenerate exists in the current messages
      const messageToRegenerate = messages.find(msg => msg.id === messageId);
      if (!messageToRegenerate) {
        console.error(`Message ID ${messageId} not found in current messages`);
        toast({
          variant: "destructive",
          title: "Error",
          description: "The message you are trying to regenerate could not be found."
        });
        return null;
      }
      
      // Check if this is the initial message (no seller messages before it)
      const messageIndex = messages.findIndex(msg => msg.id === messageId);
      const previousMessages = messages.slice(0, messageIndex);
      const sellerMessagesBefore = previousMessages.filter(msg => msg.sender_type === "seller");
      const isInitialMessage = sellerMessagesBefore.length === 0;
      
      console.log(`Regenerating message. Is initial message: ${isInitialMessage}`);
      
      // First, remove the message from UI to show it's being regenerated
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      // Call the API to regenerate the message with optional custom instructions
      const newMessage = await handleRegenerateAIResponse(
        conversation, 
        messages, 
        messageId, 
        tone,
        customInstructions
      );
      
      if (newMessage) {
        console.log("Successfully regenerated message with new ID:", newMessage.id);
        
        // Add the new message to the UI in the same position as the old one
        setMessages(prev => {
          // Create a new array with the new message at the correct position
          const newMessages = [...prev];
          newMessages.splice(messageIndex, 0, newMessage);
          return newMessages;
        });
        
        toast({
          title: "Message regenerated",
          description: tone === "custom"
            ? "The message has been successfully regenerated with your custom instructions."
            : `The message has been successfully regenerated with a ${tone} tone.`,
          variant: "default"
        });
        
        // Force a refresh to ensure we have the latest data
        await refreshMessages();
        
        return newMessage;
      } else {
        console.error("Failed to regenerate message - no new message returned");
        
        // Since regeneration failed, add the original message back
        setMessages(prev => {
          const newMessages = [...prev];
          newMessages.splice(messageIndex, 0, messageToRegenerate);
          return newMessages;
        });
        
        toast({
          variant: "destructive",
          title: "Failed to regenerate message",
          description: "There was an error regenerating the message. Please try again."
        });
        return null;
      }
    } catch (error: any) {
      console.error("Error in regenerateAIResponse:", error);
      
      // More specific error message based on the error type
      let errorMessage = "There was an error regenerating the message. Please try again.";
      
      if (error.message?.includes("not found") || error.message?.includes("no longer exists")) {
        errorMessage = "The message you are trying to regenerate no longer exists.";
      } else if (error.message?.includes("AI could not generate")) {
        errorMessage = "Our AI service couldn't generate a response. Please try again later.";
      }
      
      toast({
        variant: "destructive",
        title: "Failed to regenerate message",
        description: errorMessage,
      });
      return null;
    }
  };

  return {
    regenerateAIResponse
  };
};
